import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Loader2, Trash2, Pencil, Save as SaveIcon, X as XIcon,
  Folder, ImageOff, Leaf, Download, Share2, Copy, CheckCircle2, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { devError } from '../utils/logger';

/**
 * /projects/:projectId — single-project view. Shows the project header
 * (rename in place), a grid of saved listings, and per-listing remove
 * buttons. Deleting the project itself sends the user back to /projects.
 */
export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [busyListing, setBusyListing] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}`);
      setProject(data);
      setDraft({ name: data.name || '', description: data.description || '' });
    } catch (e) {
      devError('project load failed', e);
      setError(e.response?.data?.detail || 'Project not found');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projectId]);

  const saveEdits = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/projects/${projectId}`, draft);
      toast.success('Project updated');
      setEditing(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async () => {
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('Project deleted');
      navigate('/projects');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  const removeListing = async (listingId) => {
    setBusyListing(listingId);
    try {
      await axios.delete(`${API}/projects/${projectId}/listings/${listingId}`);
      toast.success('Removed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    } finally {
      setBusyListing(null);
    }
  };

  const shareUrlFor = (token) => `${window.location.origin}/projects/shared/${token}`;

  const exportPack = async (template = 'nabers') => {
    if (!project?.listings?.length) {
      toast.error('Add at least one item before exporting');
      return;
    }
    setExporting(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/nabers-export`, {
        params: { template },
        responseType: 'blob',
      });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${template}-pack.zip`;
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      const label = {
        'nabers': 'NABERS',
        'climate-active': 'Climate Active',
        'green-star-fitouts': 'Green Star Fitouts',
        'esp-construction': 'DCCEEW ESP Policy',
      }[template] || 'Compliance';
      toast.success(`${label} evidence pack downloaded`);
    } catch (err) {
      devError('compliance export failed', err);
      toast.error('Could not generate export');
    } finally {
      setExporting(false);
    }
  };

  const createShareLink = async () => {
    setSharing(true);
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/share`, {
        expires_in_days: 30,
      });
      await load();
      const url = shareUrlFor(data.share_token);
      try {
        await navigator.clipboard.writeText(url);
        setCopiedShare(true);
        setTimeout(() => setCopiedShare(false), 2500);
        toast.success('Share link copied — valid for 30 days');
      } catch {
        toast.success('Share link created — valid for 30 days');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create share link');
    } finally {
      setSharing(false);
    }
  };

  const copyExistingShare = async () => {
    const token = project?.share?.token;
    if (!token) return;
    try {
      await navigator.clipboard.writeText(shareUrlFor(token));
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2500);
      toast.success('Share link copied');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  const revokeShareLink = async () => {
    try {
      await axios.delete(`${API}/projects/${projectId}/share`);
      toast.success('Share link revoked');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not revoke');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen pt-24 pb-20" data-testid="project-not-found">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="font-playfair text-3xl mb-3">Project not found</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/projects"><Button className="btn-gold">Back to projects</Button></Link>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="project-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading project…
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="project-detail-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <Link to="/projects" className="text-sm text-muted-foreground hover:text-accent inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-3 h-3" /> All projects
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-10">
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2 inline-flex items-center gap-1.5">
              <Folder className="w-3 h-3" /> Project
            </p>
            {editing ? (
              <div className="space-y-3 max-w-xl" data-testid="project-edit-form">
                <input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  maxLength={80}
                  className="w-full font-playfair text-3xl bg-transparent border-b border-border/40 focus:outline-none focus:border-accent pb-1"
                  data-testid="project-edit-name"
                />
                <textarea
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  maxLength={500}
                  rows={2}
                  placeholder="Optional notes about this project…"
                  className="w-full bg-secondary border border-border/40 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
                  data-testid="project-edit-description"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveEdits} disabled={saving} className="btn-gold" data-testid="project-edit-save">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <SaveIcon className="w-4 h-4 mr-2" />} Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft({ name: project.name, description: project.description }); }}>
                    <XIcon className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <h1 className="font-playfair text-3xl md:text-4xl mb-1">{project.name}</h1>
                {project.description && <p className="text-muted-foreground max-w-2xl">{project.description}</p>}
                <p className="text-xs text-muted-foreground mt-3">
                  {(project.listings || []).length} item{(project.listings || []).length === 1 ? '' : 's'} · created {new Date(project.created_at).toLocaleDateString()}
                </p>
              </>
            )}
          </div>

          {!editing && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="project-edit-trigger">
                <Pencil className="w-4 h-4 mr-2" /> Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="project-delete-trigger">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this project?</AlertDialogTitle>
                    <AlertDialogDescription>
                      The project and its saved-item list will be removed. The listings themselves stay on PatinaMart — only this project bucket goes.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={deleteProject} className="bg-destructive hover:bg-destructive/90" data-testid="project-delete-confirm">
                      Delete project
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>

        {(project.listings && project.listings.length > 0) && (project.origin_quotas || (project.avg_mci ?? 0) > 0) && (
          <div
            className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3"
            data-testid="circularity-stats-row"
          >
            <div className="rounded-lg border border-border/30 bg-card/40 px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg MCI</p>
              <p className="text-xl font-mono text-emerald-300">{(project.avg_mci ?? 0).toFixed(2)}<span className="text-xs text-muted-foreground"> /1.00</span></p>
              <p className="text-[10px] text-muted-foreground/80">EMF method · ISO 59020</p>
            </div>
            <div className="rounded-lg border border-border/30 bg-card/40 px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Data quality</p>
              <p className="text-xl font-mono text-emerald-300">{(project.avg_dqi ?? 0).toFixed(1)}<span className="text-xs text-muted-foreground"> /3.0</span></p>
              <p className="text-[10px] text-muted-foreground/80">DGNB BRP DQI</p>
            </div>
            <div className="rounded-lg border border-border/30 bg-card/40 px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Reused share</p>
              <p className="text-xl font-mono text-emerald-300">{Math.round(project.origin_quotas?.reused_pct ?? 0)}%</p>
              <p className="text-[10px] text-muted-foreground/80">By mass · DGNB BRP</p>
            </div>
            <div className="rounded-lg border border-border/30 bg-card/40 px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total mass</p>
              <p className="text-xl font-mono text-emerald-300">{Math.round(project.origin_quotas?.total_mass_kg ?? 0).toLocaleString()}<span className="text-xs text-muted-foreground"> kg</span></p>
              <p className="text-[10px] text-muted-foreground/80">Reclaimed inventory</p>
            </div>
          </div>
        )}

        {(project.listings && project.listings.length > 0) && (
          <div
            className="mb-8 rounded-xl border border-border/30 bg-card/60 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
            data-testid="nabers-action-bar"
          >
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Leaf className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-sm md:text-base">Compliance evidence pack</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {project.total_co2e_kg != null && project.total_co2e_kg > 0 ? (
                    <>
                      <strong className="text-emerald-400">
                        {project.total_co2e_kg >= 1000
                          ? `${(project.total_co2e_kg / 1000).toFixed(2)} t`
                          : `${Math.round(project.total_co2e_kg)} kg`} CO₂e
                      </strong>{' '}avoided (A1–A3) · Green Star Fitouts · Climate Active · NABERS
                    </>
                  ) : (
                    <>Generate a ZIP mapped to Green Star Fitouts, Climate Active, or NABERS templates.</>
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={exporting}
                    className="btn-gold"
                    data-testid="project-nabers-export-btn"
                  >
                    {exporting
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Download className="w-4 h-4 mr-2" />}
                    Export compliance pack
                    <ChevronDown className="w-3.5 h-3.5 ml-2 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                    Choose framework
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => exportPack('green-star-fitouts')}
                    data-testid="export-green-star-fitouts"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">Green Star Fitouts</span>
                      <span className="text-[11px] text-muted-foreground">GBCA · Design for Reuse + Responsible Procurement</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => exportPack('climate-active')}
                    data-testid="export-climate-active"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">Climate Active</span>
                      <span className="text-[11px] text-muted-foreground">Upfront Carbon for Buildings · A1–A5</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => exportPack('esp-construction')}
                    data-testid="export-esp-construction"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">DCCEEW ESP Policy</span>
                      <span className="text-[11px] text-muted-foreground">Commonwealth construction procurements ≥ A$7.5M</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => exportPack('nabers')}
                    data-testid="export-nabers"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">NABERS</span>
                      <span className="text-[11px] text-muted-foreground">Embodied Emissions</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {project.share?.token ? (
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    onClick={copyExistingShare}
                    data-testid="project-share-copy-btn"
                  >
                    {copiedShare
                      ? <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-400" />
                      : <Copy className="w-4 h-4 mr-2" />}
                    {copiedShare ? 'Copied' : 'Copy assessor link'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={revokeShareLink}
                    title="Revoke share link"
                    data-testid="project-share-revoke-btn"
                  >
                    <XIcon className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={createShareLink}
                  disabled={sharing}
                  data-testid="project-share-create-btn"
                >
                  {sharing
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Share2 className="w-4 h-4 mr-2" />}
                  Share with assessor
                </Button>
              )}
            </div>
          </div>
        )}

        {(!project.listings || project.listings.length === 0) ? (
          <div className="bg-card border border-border/30 rounded-xl p-12 text-center" data-testid="project-empty">
            <Folder className="w-10 h-10 text-accent/50 mx-auto mb-3" />
            <h2 className="font-playfair text-xl mb-2">Nothing saved here yet</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-5">
              Browse the marketplace and tap <strong className="text-foreground">"Save to project"</strong> on any listing to drop it into this project.
            </p>
            <Link to="/marketplace"><Button className="btn-gold">Browse marketplace</Button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {project.listings.map((l) => (
              <div
                key={l.listing_id}
                className="group rounded-xl overflow-hidden border border-border/30 bg-card relative"
                data-testid={`project-listing-${l.listing_id}`}
              >
                <Link to={`/listing/${l.listing_id}`}>
                  <div
                    className="aspect-[4/3] bg-secondary/40 bg-cover bg-center flex items-center justify-center"
                    style={l.image_url ? { backgroundImage: `url(${l.image_url})` } : {}}
                  >
                    {!l.image_url && <ImageOff className="w-8 h-8 text-muted-foreground/30" />}
                  </div>
                  <div className="p-3">
                    <p className="font-medium text-sm line-clamp-2 group-hover:text-accent transition-colors">{l.title}</p>
                    <p className="text-xs text-muted-foreground capitalize mt-1">{l.category}</p>
                    {l.price != null && (
                      <p className="text-sm text-accent font-mono mt-2">
                        ${Number(l.price).toLocaleString()}
                        <span className="text-xs text-muted-foreground">/{l.unit || 'piece'}</span>
                      </p>
                    )}
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => removeListing(l.listing_id)}
                  disabled={busyListing === l.listing_id}
                  className="absolute top-2 right-2 w-8 h-8 rounded-md bg-background/80 backdrop-blur-sm border border-border/50 flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors"
                  title="Remove from project"
                  data-testid={`project-remove-${l.listing_id}`}
                >
                  {busyListing === l.listing_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XIcon className="w-3.5 h-3.5" />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
